// Leaderboard and statistics management
const STORAGE_KEY = 'chess_leaderboard_data';

// Default player stats structure
const createDefaultStats = () => ({
  gamesPlayed: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  winRate: 0,
  totalPlayTime: 0,
  avgGameTime: 0,
  longestWinStreak: 0,
  currentWinStreak: 0,
  fastestWin: null,
  totalMoves: 0,
  avgMovesPerGame: 0,
  checksDelivered: 0,
  checkmatesDelivered: 0,
  piecesCaptures: 0,
  timeouts: 0,
  rating: 1200, // ELO-style rating
  achievements: [],
  lastPlayed: null,
  createdAt: Date.now()
});

// Achievement definitions
export const ACHIEVEMENTS = {
  FIRST_WIN: {
    id: 'first_win',
    name: 'First Victory',
    description: 'Win your first game',
    icon: '🏆',
    condition: (stats) => stats.wins >= 1
  },
  SPEED_DEMON: {
    id: 'speed_demon',
    name: 'Speed Demon',
    description: 'Win a game in under 5 minutes',
    icon: '⚡',
    condition: (stats) => stats.fastestWin && stats.fastestWin < 300000
  },
  WIN_STREAK_5: {
    id: 'win_streak_5',
    name: 'Hot Streak',
    description: 'Win 5 games in a row',
    icon: '🔥',
    condition: (stats) => stats.longestWinStreak >= 5
  },
  WIN_STREAK_10: {
    id: 'win_streak_10',
    name: 'Unstoppable',
    description: 'Win 10 games in a row',
    icon: '🚀',
    condition: (stats) => stats.longestWinStreak >= 10
  },
  VETERAN: {
    id: 'veteran',
    name: 'Veteran Player',
    description: 'Play 50 games',
    icon: '🎖️',
    condition: (stats) => stats.gamesPlayed >= 50
  },
  MASTER: {
    id: 'master',
    name: 'Chess Master',
    description: 'Reach 1600 rating',
    icon: '👑',
    condition: (stats) => stats.rating >= 1600
  },
  GRANDMASTER: {
    id: 'grandmaster',
    name: 'Grandmaster',
    description: 'Reach 2000 rating',
    icon: '💎',
    condition: (stats) => stats.rating >= 2000
  },
  PERFECTIONIST: {
    id: 'perfectionist',
    name: 'Perfectionist',
    description: 'Win 10 games without a single loss',
    icon: '✨',
    condition: (stats) => stats.wins >= 10 && stats.losses === 0
  }
};

// Get leaderboard data from localStorage
export const getLeaderboardData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error loading leaderboard data:', error);
    return {};
  }
};

// Save leaderboard data to localStorage
export const saveLeaderboardData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving leaderboard data:', error);
  }
};

// Get or create player stats
export const getPlayerStats = (playerName) => {
  const data = getLeaderboardData();
  if (!data[playerName]) {
    data[playerName] = createDefaultStats();
    saveLeaderboardData(data);
  }
  return data[playerName];
};

// Calculate ELO rating change
const calculateRatingChange = (playerRating, opponentRating, result, kFactor = 32) => {
  const expectedScore = 1 / (1 + Math.pow(10, (opponentRating - playerRating) / 400));
  return Math.round(kFactor * (result - expectedScore));
};

// Record game result
export const recordGameResult = (gameData) => {
  const {
    playerName,
    opponent,
    result, // 'win', 'loss', 'draw'
    gameMode,
    gameDuration,
    totalMoves,
    checksDelivered = 0,
    checkmatesDelivered = 0,
    piecesCaptures = 0,
    endReason // 'checkmate', 'stalemate', 'timeout'
  } = gameData;

  const data = getLeaderboardData();
  const playerStats = getPlayerStats(playerName);
  
  // Update basic stats
  playerStats.gamesPlayed++;
  playerStats.totalPlayTime += gameDuration;
  playerStats.avgGameTime = playerStats.totalPlayTime / playerStats.gamesPlayed;
  playerStats.totalMoves += totalMoves;
  playerStats.avgMovesPerGame = playerStats.totalMoves / playerStats.gamesPlayed;
  playerStats.checksDelivered += checksDelivered;
  playerStats.checkmatesDelivered += checkmatesDelivered;
  playerStats.piecesCaptures += piecesCaptures;
  playerStats.lastPlayed = Date.now();

  // Handle timeouts
  if (endReason === 'timeout' && result === 'loss') {
    playerStats.timeouts++;
  }

  // Update win/loss/draw stats
  if (result === 'win') {
    playerStats.wins++;
    playerStats.currentWinStreak++;
    playerStats.longestWinStreak = Math.max(
      playerStats.longestWinStreak,
      playerStats.currentWinStreak
    );
    
    // Track fastest win
    if (!playerStats.fastestWin || gameDuration < playerStats.fastestWin) {
      playerStats.fastestWin = gameDuration;
    }
  } else {
    playerStats.currentWinStreak = 0;
    if (result === 'loss') {
      playerStats.losses++;
    } else if (result === 'draw') {
      playerStats.draws++;
    }
  }

  // Calculate win rate
  playerStats.winRate = (playerStats.wins / playerStats.gamesPlayed) * 100;

  // Update rating (simplified ELO for computer games)
  if (gameMode === 'computer') {
    const computerRating = 1400; // Base computer rating
    const resultScore = result === 'win' ? 1 : result === 'draw' ? 0.5 : 0;
    const ratingChange = calculateRatingChange(playerStats.rating, computerRating, resultScore);
    playerStats.rating += ratingChange;
    playerStats.rating = Math.max(800, Math.min(2800, playerStats.rating)); // Clamp between 800-2800
  }

  // Check for new achievements
  checkAchievements(playerStats);

  // Save updated data
  data[playerName] = playerStats;
  saveLeaderboardData(data);

  return playerStats;
};

// Check and award achievements
const checkAchievements = (playerStats) => {
  const newAchievements = [];
  
  Object.values(ACHIEVEMENTS).forEach(achievement => {
    if (!playerStats.achievements.includes(achievement.id) && 
        achievement.condition(playerStats)) {
      playerStats.achievements.push(achievement.id);
      newAchievements.push(achievement);
    }
  });

  return newAchievements;
};

// Get leaderboard rankings
export const getLeaderboard = (sortBy = 'rating', limit = 10) => {
  const data = getLeaderboardData();
  const players = Object.entries(data).map(([name, stats]) => ({
    name,
    ...stats
  }));

  // Sort by specified criteria
  players.sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'wins':
        return b.wins - a.wins;
      case 'winRate':
        return b.winRate - a.winRate;
      case 'gamesPlayed':
        return b.gamesPlayed - a.gamesPlayed;
      case 'winStreak':
        return b.longestWinStreak - a.longestWinStreak;
      default:
        return b.rating - a.rating;
    }
  });

  return players.slice(0, limit);
};

// Get player rank
export const getPlayerRank = (playerName, sortBy = 'rating') => {
  const leaderboard = getLeaderboard(sortBy, 1000);
  const rank = leaderboard.findIndex(player => player.name === playerName);
  return rank === -1 ? null : rank + 1;
};

// Clear all leaderboard data
export const clearLeaderboard = () => {
  localStorage.removeItem(STORAGE_KEY);
};

// Export player data
export const exportPlayerData = (playerName) => {
  const stats = getPlayerStats(playerName);
  const dataStr = JSON.stringify(stats, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  
  const link = document.createElement('a');
  link.href = URL.createObjectURL(dataBlob);
  link.download = `${playerName}_chess_stats.json`;
  link.click();
};